import { default as React } from 'react';
import { useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Grid } from '@material-ui/core';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { isArgocdAvailable } from '@roadiehq/backstage-plugin-argo-cd';
import { MicroappConfigCard } from './MicroappConfigCard';
import { isUserAuthorizedForEntity, useSkyStageAdminEmails } from './hooks';
import { EnvironmentAwareArgoCDCard } from './EnvironmentAwareArgoCDCard';

const MICROAPP_ANNOTATION_KEY = 'skystage.wellsky.com/type';
const MICROAPP_ANNOTATION_VALUE = 'microapp';

/**
 * This component displays cards beside the About card for microapps:
 * - Shows Config card if user is the owner (any app type)
 * - Shows ArgoCD card for Hosted apps when user is NOT the owner
 * - Falls back to ArgoCD for non-Hosted apps when user is NOT the owner (if available)
 *
 * Note: For Hosted apps where user IS the owner, ArgoCD card is shown below (in EntityPage.tsx)
 */
export const MicroappSecondaryCard = ({
  variant,
}: {
  variant?: 'gridItem';
}) => {
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);
  const adminEmails = useSkyStageAdminEmails();
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');

  // Get current user's email from identity API
  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const credentials = await identityApi.getCredentials();

        if (credentials.token) {
          const tokenParts = credentials.token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));

            if (payload.email) {
              setCurrentUserEmail(payload.email);
            } else if (payload.sub) {
              const match = payload.sub.match(/user:(?:default|[^/]+)\/(.+)/);
              if (match && match[1]) {
                const localPart = match[1];
                setCurrentUserEmail(`${localPart}@wellsky.com`);
              }
            }
          }
        }
      } catch (error) {
        // Silently handle errors
      }
    };

    fetchUserEmail();
  }, [identityApi]);

  const isMicroapp =
    entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
    MICROAPP_ANNOTATION_VALUE;

  const appType = entity.metadata.annotations?.['microapp/appType'];
  const isHostedApp = appType === 'Hosted';

  // Authorized if the user owns/created the app or is a SkyStage admin.
  const isAuthorized = isUserAuthorizedForEntity(
    entity,
    currentUserEmail,
    adminEmails,
  );

  // Rename for clarity (backward compatibility)
  const isCreator = isAuthorized;

  // For microapps
  if (isMicroapp) {
    // Priority 1: If user is creator, show Config card (for any app type)
    if (isCreator) {
      return (
        <Grid item md={6} xs={12}>
          <MicroappConfigCard variant={variant} />
        </Grid>
      );
    }

    // Priority 2: If Hosted app and user is NOT creator, show ArgoCD
    // IDP-1175: Use production environment for non-owner view (they only see production)
    if (isHostedApp && isArgocdAvailable(entity)) {
      return (
        <Grid item md={6} xs={12}>
          <EnvironmentAwareArgoCDCard environment="production" title="ArgoCD" />
        </Grid>
      );
    }

    // Priority 3: For non-Hosted apps where user is NOT creator, show ArgoCD if available
    if (isArgocdAvailable(entity)) {
      return (
        <Grid item md={6} xs={12}>
          <EnvironmentAwareArgoCDCard environment="production" title="ArgoCD" />
        </Grid>
      );
    }
  }

  // Don't render anything if not a microapp or no cards to show
  return null;
};
